import React, { useState, useEffect } from 'react';
import StatCard from '../components/dashboard/StatCard';
import RevenueChart from '../components/dashboard/RevenueChart';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import { Building2, ShieldCheck, Users, MessageSquare, Plus, Zap, RefreshCw } from 'lucide-react';
import { companyApi } from '../api/companyApi';
import { adminApi } from '../api/adminApi';
import { employeeApi } from '../api/employeeApi';
import { chatApi } from '../api/chatApi';
import { useSocket } from '../hooks/useSocket';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    companies: 0,
    admins: 0,
    employees: 0,
    activeChats: 0
  });
  const [loading, setLoading] = useState(true);
  const { socket } = useSocket();
  const navigate = useNavigate();

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const [compRes, adminRes, empRes, chatRes] = await Promise.all([
        companyApi.getCompanies({ limit: 1 }),
        adminApi.getAdmins({ limit: 1 }),
        employeeApi.getEmployees({ limit: 1 }),
        chatApi.getChatAdmins().catch(() => ({ data: { admins: [] } }))
      ]);

      setStats({
        companies: compRes?.data?.pagination?.total || compRes?.pagination?.total || 0,
        admins: adminRes?.data?.pagination?.total || adminRes?.pagination?.total || 0,
        employees: empRes?.data?.pagination?.total || empRes?.pagination?.total || 0,
        activeChats: chatRes?.data?.admins?.length || chatRes?.admins?.length || 0
      });
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  return (
    <div className="flex flex-col gap-6 w-full pb-8 animate-fadeIn">

      {/* Page Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 rounded-2xl bg-white/90 backdrop-blur-xl border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.03)]">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight font-display">Overview</h1>
            <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/60 text-[10px] font-extrabold text-emerald-700 tracking-wide uppercase">
              <Zap className="w-3 h-3 text-emerald-600" />
              Real-time Active
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Multi-tenant operations, employee allocation caps, and telemetry insights.</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchDashboardStats}
            title="Refresh Metrics"
            className="p-2.5 rounded-xl border border-slate-200/80 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-emerald-600' : ''}`} />
          </button>

          <button
            onClick={() => navigate('/companies')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white font-bold text-xs shadow-lg shadow-emerald-600/30 hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4" />
            <span>Onboard Company</span>
          </button>
        </div>
      </div>

      {/* Executive Stat Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Companies"
          value={loading ? '...' : stats.companies}
          icon={Building2}
          color="green"
          trend="+14.2%"
        />
        <StatCard
          title="Total Admins"
          value={loading ? '...' : stats.admins}
          icon={ShieldCheck}
          color="emerald"
          trend="+8.5%"
        />
        <StatCard
          title="Managed Employees"
          value={loading ? '...' : stats.employees}
          icon={Users}
          color="teal"
          trend="+22.4%"
        />
        <StatCard
          title="Active Admin Chats"
          value={loading ? '...' : stats.activeChats}
          icon={MessageSquare}
          isLive={true}
          color="amber"
        />
      </div>

      {/* Interactive Chart + Live System Stream */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 h-[440px] min-h-0">
          <RevenueChart />
        </div>
        <div className="h-[440px] min-h-0">
          <ActivityFeed socket={socket} />
        </div>
      </div>

    </div>
  );
};

export default Dashboard;

